// ==========================================================================
// MAIN ENTRY POINT — imports all modules
// ==========================================================================

import '../sass/index.scss';
import 'swiper/css';

import gsap from 'gsap';
import { Flip } from 'gsap/flip';
gsap.registerPlugin(Flip);

import { initNav } from './nav.js';
import { initContextualNav } from './contextual-nav.js';
import { initSlider } from './slider.js';
import { initStudio } from './studio.js';
import { initHome } from './home.js';
import { initProject } from './project.js';
import { initHeaderTheme } from './header-theme.js';

// ── Alpine components — registered before Alpine initializes ──────────────────

document.addEventListener('alpine:init', () => {

  Alpine.store('mobileNav', { open: false });

  // Projects — global store, accessible from any element (nav + main)
  Alpine.store('projects', {
    view: 'grid',
    activeCategory: 'all',
    activeTags: [],
    showFilters: false,
    hoverImage: '',
    hoverId: '',
    hoverNavColor: 'dark',
    isTouch: window.matchMedia('(pointer: coarse)').matches,
    tooltipVisible: false,
    tooltipTitle: '',
    tooltipLocation: '',
    tooltipCategories: [],
    tooltipColor: 'white',
    activeSlug: '',
    activeUrl: '',
    activeThumb: '',

    selectProject(slug, url, thumb) {
      if (this.activeSlug === slug) {
        window.location.href = url;
      } else {
        this.activeSlug = slug;
        this.activeUrl  = url;
        this.activeThumb = thumb;
      }
    },

    clearActive() {
      this.activeSlug  = '';
      this.activeUrl   = '';
      this.activeThumb = '';
    },

    toggleTag(tag) {
      this.activeTags = this.activeTags.includes(tag)
        ? this.activeTags.filter(t => t !== tag)
        : [...this.activeTags, tag];
    },

    selectedCategoryLabel() {
      if (this.activeCategory === 'all') return 'All';
      return this.activeCategory.charAt(0).toUpperCase() + this.activeCategory.slice(1);
    },

    isCardVisible(projectType, categories = []) {
      return this.activeCategory === 'all'
        || this.activeCategory === projectType
        || categories.includes(this.activeCategory);
    },

    isCardDimmedByTags(tags) {
      if (this.activeTags.length === 0) return false;
      return !this.activeTags.every(t => tags.includes(t));
    },
  });

  // Scrolling text — overflow marquee on hover/touch
  Alpine.data('scrollingText', () => ({
    _anim: null,

    start() {
      const inner   = this.$refs.inner;
      const wrapper = this.$el;
      const overflow = inner.scrollWidth - wrapper.clientWidth;
      if (overflow <= 0) return;
      if (this._anim) this._anim.kill();
      this._anim = gsap.to(inner, {
        x: -overflow,
        duration: overflow / 60,
        ease: 'none',
        delay: 0.25,
      });
    },

    reset() {
      const inner = this.$refs.inner;
      if (this._anim) this._anim.kill();
      this._anim = gsap.to(inner, { x: 0, duration: 0.3, ease: 'power2.out' });
    },
  }));

  // Articles — global store for grid-articles page
  Alpine.store('articles', {
    activeCategory: 'all',
    tooltipVisible: false,
    tooltipTitle: '',
    tooltipDate: '',
    tooltipCategories: [],
    tooltipColor: 'white',

    selectedCategoryLabel() {
      if (this.activeCategory === 'all') return 'All';
      return this.activeCategory.charAt(0).toUpperCase() + this.activeCategory.slice(1);
    },

    isCardVisible(categories = []) {
      return this.activeCategory === 'all' || categories.includes(this.activeCategory);
    },
  });

  // Articles overlay — split-screen on desktop, direct nav on mobile
  Alpine.data('articlesOverlay', () => ({
    isOpen: false,
    loading: false,
    content: '',
    currentUrl: null,
    coverUrl: '',
    overlayDate: '',
    overlayTitle: '',

    init() {
      window.addEventListener('popstate', () => {
        if (this.isOpen) {
          this.isOpen = false;
          this.content = '';
          this.currentUrl = null;
          document.body.classList.remove('articles-overlay-open');
        }
      });
    },

    async openArticle(url, coverUrl, date, title) {
      if (window.matchMedia('(max-width: 768px)').matches) {
        if (document.body.dataset.page === 'ideas') {
          const safeUrl = url.replace(/"/g, '\\"');
          const panel = document.querySelector(`.ideas-inline-panel[data-url="${safeUrl}"]`);
          if (!panel) { window.location.href = url; return; }

          const wasOpen = panel.classList.contains('is-open');

          document.querySelectorAll('.ideas-inline-panel.is-open').forEach(p => {
            p.classList.remove('is-open');
            p.addEventListener('transitionend', () => { p.innerHTML = ''; }, { once: true });
          });

          if (wasOpen) return;

          panel.innerHTML = '<div class="ideas-inline-panel__body"><div class="ideas-inline-panel__loading">Loading…</div></div>';
          panel.classList.add('is-open');

          try {
            const res = await fetch(`${url}?partial=1`, {
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!res.ok) throw new Error();
            panel.innerHTML = `<div class="ideas-inline-panel__body">${await res.text()}</div>`;
          } catch {
            panel.innerHTML = '<div class="ideas-inline-panel__body"><p style="padding:1rem">Unable to load article.</p></div>';
          }
          return;
        }
        window.location.href = url;
        return;
      }

      if (this.currentUrl === url && this.isOpen) return;

      this.isOpen = true;
      this.loading = true;
      this.content = '';
      this.currentUrl = url;
      this.coverUrl = coverUrl || '';
      this.overlayDate = date || '';
      this.overlayTitle = title || '';

      try {
        const res = await fetch(`${url}?partial=1`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error('Failed to load article');
        this.content = await res.text();
      } catch {
        this.content = '<p>Unable to load article.</p>';
      } finally {
        this.loading = false;
      }

      history.pushState({ url }, '', url);
      document.body.classList.add('articles-overlay-open');
    },

    closeArticle() {
      this.isOpen = false;
      this.content = '';
      this.currentUrl = null;
      document.body.classList.remove('articles-overlay-open');
      history.back();
    },
  }));

  // Ideas overlay
  Alpine.data('ideasOverlay', () => ({
    isOpen: false,
    loading: false,
    content: '',
    currentUrl: null,

    init() {
      window.addEventListener('popstate', () => {
        if (this.isOpen) {
          this.isOpen = false;
          this.content = '';
          this.currentUrl = null;
          document.body.classList.remove('overlay-open');
        }
      });
    },

    async openArticle(url) {
      if (this.currentUrl === url && this.isOpen) return;
      this.isOpen = true;
      this.loading = true;
      this.content = '';
      this.currentUrl = url;

      try {
        const res = await fetch(`${url}?partial=1`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error('Failed to load article');
        this.content = await res.text();
      } catch {
        this.content = '<p>Unable to load article.</p>';
      } finally {
        this.loading = false;
      }

      history.pushState({ url }, '', url);
      document.body.classList.add('overlay-open');
    },

    closeArticle() {
      this.isOpen = false;
      this.content = '';
      this.currentUrl = null;
      document.body.classList.remove('overlay-open');
      history.back();
    },
  }));

});

// ── dvh fallback — sets --vh for browsers without native dvh support ──────────
function setVh() {
  document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
}
setVh();
window.addEventListener('resize', setVh, { passive: true });

// ── Init ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initNav();
  initContextualNav();

  const isProjects = document.body.dataset.page === 'projects';

  // Body scroll lock when mobile menu is open — disabled on projects page
  Alpine.effect(() => {
    const open = Alpine.store('mobileNav').open;
    if (!isProjects && window.matchMedia('(max-width: 768px)').matches) {
      document.body.style.overflow = open ? 'hidden' : '';
    }
  });

  // Click-outside (mobile only): clear active project in list view
  if (isProjects) {
    document.addEventListener('click', (e) => {
      if (!window.matchMedia('(max-width: 768px)').matches) return;
      if (e.target.closest('.project-row') || e.target.closest('#list-thumb')) return;
      Alpine.store('projects').clearActive();
    });
  }

  const page = document.body.dataset.page;

  if (page === 'home')    { const swiper = initSlider(); initHome(swiper); }
  if (page === 'studio')  initStudio();
  if (page === 'project') initProject();
  initHeaderTheme();

  // Tooltip follow cursor — projects listing + single project (desktop only)
  if (page === 'projects' || page === 'project') {
    const tooltip = document.getElementById('project-tooltip');
    if (tooltip) {
      document.addEventListener('mousemove', (e) => {
        tooltip.style.left = (e.clientX + 18) + 'px';
        tooltip.style.top  = (e.clientY + 18) + 'px';
      }, { passive: true });
    }
  }

  if (page === 'projects') {
    const nav = document.getElementById('nav');
    if (nav) {
      Alpine.effect(() => {
        const isList = Alpine.store('projects').view === 'list';
        if (isList) {
          nav.classList.add('nav--scrolled');
        } else if (window.scrollY <= 80) {
          nav.classList.remove('nav--scrolled');
        }
      });
    }
    initProjectFilter();
    warmGridImages();
    initTagsScroll();
    initProjectsTagsScroll();
  }

  // Tooltip follow cursor — articles grid (desktop only)
  if (page === 'grid-articles' || page === 'ideas') {
    const tooltip = document.getElementById('article-tooltip');
    if (tooltip) {
      document.addEventListener('mousemove', (e) => {
        tooltip.style.left = (e.clientX + 18) + 'px';
        tooltip.style.top  = (e.clientY + 18) + 'px';
      }, { passive: true });
    }
    if (page === 'grid-articles') initArticleFilter();
    if (page === 'ideas') initIdeasMobile();
  }
});

function initIdeasMobile() {
  if (!window.matchMedia('(max-width: 768px)').matches) return;

  const label = document.getElementById('ideas-nav-label');
  if (!label) return;

  const cards = [...document.querySelectorAll('.ideas-page .article-card')];
  if (!cards.length) return;

  const baseText = label.textContent.trim();

  function updateLabel() {
    const threshold = 60;
    let current = null;
    for (const card of cards) {
      if (card.getBoundingClientRect().top <= threshold) {
        current = card;
      } else {
        break;
      }
    }
    label.textContent = current
      ? `${baseText}: ${current.dataset.title}`
      : baseText;
  }

  window.addEventListener('scroll', updateLabel, { passive: true });
  updateLabel();
}

function initArticleFilter() {
  const grid = document.querySelector('.articles__grid-inner');
  if (!grid) return;

  let isFirst = true;

  Alpine.effect(() => {
    const store = Alpine.store('articles');
    void store.activeCategory;

    const cards = [...grid.querySelectorAll('.article-card')];

    if (isFirst) {
      isFirst = false;
      cards.forEach(card => {
        const visible = store.isCardVisible(JSON.parse(card.dataset.categories || '[]'));
        card.style.display = visible ? '' : 'none';
      });
      return;
    }

    const state = Flip.getState(cards);

    cards.forEach(card => {
      const visible = store.isCardVisible(JSON.parse(card.dataset.categories || '[]'));
      card.style.display = visible ? '' : 'none';
    });

    Flip.from(state, {
      duration: 0.55,
      ease: 'power2.inOut',
      absolute: true,
      onEnter: els => gsap.fromTo(els,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, delay: 0.3 }
      ),
      onLeave: els => gsap.to(els,
        { opacity: 0, duration: 0.2 }
      ),
    });
  });
}

function initTagsScroll() {
  const scroll = document.querySelector('.nav__filter-tags-scroll');
  if (!scroll) return;

  const prev = document.querySelector('.nav__filter-arrow--prev');
  const next = document.querySelector('.nav__filter-arrow--next');

  // Center tags when they fit; left-align when they overflow (so scroll works)
  function updateLayout() {
    const overflowing = scroll.scrollWidth > scroll.clientWidth + 1;
    scroll.style.justifyContent = overflowing ? '' : 'center';
  }

  function updateArrows() {
    updateLayout();
    prev?.classList.toggle('is-visible', scroll.scrollLeft > 0);
    next?.classList.toggle('is-visible', scroll.scrollLeft < scroll.scrollWidth - scroll.clientWidth - 1);
  }

  scroll.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', updateArrows);

  // Re-check after the filter panel's CSS transition ends
  Alpine.effect(() => {
    void Alpine.store('projects').showFilters;
    setTimeout(updateArrows, 250);
  });

  // Arrow buttons — smooth scroll only here, not on CSS
  prev?.addEventListener('click', () => scroll.scrollBy({ left: -200, behavior: 'smooth' }));
  next?.addEventListener('click', () => scroll.scrollBy({ left:  200, behavior: 'smooth' }));

  // Drag to scroll — instant, no smoothing
  let dragging = false;
  let startX = 0;
  let startScrollLeft = 0;
  let rafId = null;
  let targetScrollLeft = 0;

  scroll.addEventListener('mousedown', (e) => {
    dragging = true;
    scroll.classList.add('is-dragging');
    startX = e.clientX;
    startScrollLeft = scroll.scrollLeft;
    targetScrollLeft = scroll.scrollLeft;
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    targetScrollLeft = startScrollLeft - (e.clientX - startX);
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      scroll.scrollLeft = targetScrollLeft;
      rafId = null;
    });
  });

  document.addEventListener('mouseup', () => {
    if (!dragging) return;
    dragging = false;
    scroll.classList.remove('is-dragging');
    rafId = null;
  });
}

function initProjectsTagsScroll() {
  const scroll = document.querySelector('.projects__filter-scroll');
  if (!scroll) return;

  const prev = document.querySelector('.projects__filter-arrow--prev');
  const next = document.querySelector('.projects__filter-arrow--next');

  function updateArrows() {
    prev?.classList.toggle('is-visible', scroll.scrollLeft > 0);
    next?.classList.toggle('is-visible', scroll.scrollLeft < scroll.scrollWidth - scroll.clientWidth - 1);
  }

  scroll.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', updateArrows);

  Alpine.effect(() => {
    void Alpine.store('projects').showFilters;
    setTimeout(updateArrows, 250);
  });

  prev?.addEventListener('click', () => scroll.scrollBy({ left: -200, behavior: 'smooth' }));
  next?.addEventListener('click', () => scroll.scrollBy({ left:  200, behavior: 'smooth' }));

  let dragging = false, startX = 0, startScrollLeft = 0, rafId = null, targetScrollLeft = 0;

  scroll.addEventListener('mousedown', (e) => {
    dragging = true;
    scroll.classList.add('is-dragging');
    startX = e.clientX;
    startScrollLeft = scroll.scrollLeft;
    targetScrollLeft = scroll.scrollLeft;
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    targetScrollLeft = startScrollLeft - (e.clientX - startX);
    if (rafId) return;
    rafId = requestAnimationFrame(() => { scroll.scrollLeft = targetScrollLeft; rafId = null; });
  });

  document.addEventListener('mouseup', () => {
    if (!dragging) return;
    dragging = false;
    scroll.classList.remove('is-dragging');
    rafId = null;
  });
}

// ── Grid images: warm the lazy ones once the page is idle ────────────────────
// Category filtering hides cards with `display: none` (see initProjectFilter),
// so their images — lazy and never in the viewport — have not been fetched.
// Clicking a category teleports those cards to the top of the grid and only
// THEN starts the download: on a slow connection the revealed cells stay empty
// for seconds. Promoting them to eager after load pulls the same bytes the user
// would fetch on scroll anyway, just early and off the critical path.
function warmGridImages() {
  const imgs = [...document.querySelectorAll('.projects__grid-inner img[loading="lazy"]')];
  if (!imgs.length) return;

  const warm = () => imgs.forEach(img => { img.loading = 'eager'; });

  const schedule = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2000 });
    else setTimeout(warm, 500);
  };

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

function initProjectFilter() {
  const grid = document.querySelector('.projects__grid-inner');
  if (!grid) return;

  let isFirst = true;

  // ── Category: show / hide with Flip animation ──────────────────────────────
  Alpine.effect(() => {
    const store = Alpine.store('projects');
    void store.activeCategory;

    const cards = [...grid.querySelectorAll('.project-card')];

    if (isFirst) {
      isFirst = false;
      cards.forEach(card => {
        const visible = store.isCardVisible(
          card.dataset.type,
          JSON.parse(card.dataset.categories || '[]')
        );
        card.style.display = visible ? '' : 'none';
      });
      return;
    }

    const state = Flip.getState(cards);

    cards.forEach(card => {
      const visible = store.isCardVisible(
        card.dataset.type,
        JSON.parse(card.dataset.categories || '[]')
      );
      card.style.display = visible ? '' : 'none';
    });

    Flip.from(state, {
      duration: 0.55,
      ease: 'power2.inOut',
      absolute: true,
      onEnter: els => gsap.fromTo(els,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, delay: 0.3 }
      ),
      onLeave: els => gsap.to(els,
        { opacity: 0, duration: 0.2 }
      ),
    });
  });

  // ── Tags: dim / undim with AND (intersection) logic ──────────────────────
  Alpine.effect(() => {
    const store = Alpine.store('projects');
    void store.activeTags.slice();

    grid.querySelectorAll('.project-card').forEach(card => {
      const tags = JSON.parse(card.dataset.tags || '[]');
      card.classList.toggle('is-dimmed', store.isCardDimmedByTags(tags));
    });
  });
}
